import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import AsyncStorage from '@react-native-async-storage/async-storage'
import * as AuthSession from 'expo-auth-session'
import { globalConnectionParams } from '../GlobalConnectionParams'

type SessionStore = {
  //App launch count
  appLaunchCount: number
  incrementAppLaunchCount: () => void

  // Token configuration
  tokenConfig: AuthSession.TokenResponseConfig | undefined
  hasRehydrated: boolean

  // Refresh queue for deduplication
  isRefreshing: boolean
  failedQueue: ((token: AuthSession.TokenResponseConfig) => void)[]

  // Token actions
  setTokenConfig: (config: AuthSession.TokenResponseConfig | undefined) => void
  clearTokenConfig: () => void
  getAccessToken: () => string | undefined
  getIdToken: () => string | undefined
  getRefreshToken: () => string | undefined

  // Computed selectors
  isLoggedIn: () => boolean
  isExpired: () => boolean
  shouldRefresh: () => boolean

  // Refresh queue actions
  setRefreshing: (value: boolean) => void
  addToQueue: (callback: (token: AuthSession.TokenResponseConfig) => void) => void
  processQueue: (token: AuthSession.TokenResponseConfig) => void
  clearQueue: () => void
}

export const useSessionStore = create<SessionStore>()(
  persist(
    (set, get) => ({
      //App launch count
      appLaunchCount: 0,
      incrementAppLaunchCount: () => {
        set((state) => ({ appLaunchCount: state.appLaunchCount + 1 }))
        console.log('appLaunchCount '+get().appLaunchCount)
      },

      // Token state
      tokenConfig: undefined,
      hasRehydrated: false,

      // Refresh queue state
      isRefreshing: false,
      failedQueue: [],

      // Token actions
      setTokenConfig: (config) => {
        set({ tokenConfig: config })
        // Sync with globalConnectionParams for WebSocket authentication
        globalConnectionParams.token = config?.accessToken
      },

      clearTokenConfig: () => {
        set({ tokenConfig: undefined })
        globalConnectionParams.token = undefined
      },

      getAccessToken: () => get().tokenConfig?.accessToken,

      getIdToken: () => get().tokenConfig?.idToken,

      getRefreshToken: () => get().tokenConfig?.refreshToken,

      // Computed selectors
      isLoggedIn: () => {
        const { tokenConfig } = get()
        return tokenConfig?.accessToken !== undefined
      },

      isExpired: () => {
        const { tokenConfig } = get()
        if (!tokenConfig?.issuedAt) return false
        if (!tokenConfig?.expiresIn) return false

        const now = Date.now() / 1000 // Convert to seconds
        const expiresAt = tokenConfig.issuedAt + tokenConfig.expiresIn
        return now >= expiresAt
      },

      shouldRefresh: () => {
        const { tokenConfig } = get()
        if (!tokenConfig?.issuedAt) return false
        if (!tokenConfig?.expiresIn) return false

        const now = Date.now() / 1000 // Convert to seconds
        const expiresAt = tokenConfig.issuedAt + tokenConfig.expiresIn
        const refreshBuffer = 5 * 60 // 5 minutes in seconds

        return now >= expiresAt - refreshBuffer
      },

      // Refresh queue actions
      setRefreshing: (value: boolean) => set({ isRefreshing: value }),

      addToQueue: (callback) =>
        set((state) => ({
          failedQueue: [...state.failedQueue, callback]
        })),

      processQueue: (token) => {
        const { failedQueue } = get()
        failedQueue.forEach((resolve) => resolve(token))
        set({ failedQueue: [] })
      },

      clearQueue: () => set({ failedQueue: [] })
    }),
    {
      name: 'token-config-storage',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        tokenConfig: state.tokenConfig,
        appLaunchCount: state.appLaunchCount
      }),
      onRehydrateStorage: () => (state) => {
        // Set hasRehydrated flag when rehydration completes
        if (state) {
          state.hasRehydrated = true
          // Sync globalConnectionParams on rehydration
          if (state.tokenConfig?.accessToken) {
            globalConnectionParams.token = state.tokenConfig.accessToken
          }
        }
      },
    }
  )
)
